import React, { useCallback } from "react";
import ZForm from "@organisms/ZForm";
import {
	EWidgets,
	IFormFieldProps,
	IFormProps,
	IFormSectionProps
} from "@organisms/ZForm/interface";
import { ICommentUIProps, IRelationOption } from "../interface";
import ZIcon, { Icons } from "@atoms/ZIcon";
import { ZomentumEntities } from "@/Contracts";
import { EColors } from "@atoms/ZColors/interface";
import ZButton from "@atoms/ZButton";
import ZTypography from "@atoms/ZTypography";
import { Attachment } from "@/Contracts/Others/RecentActivityFeed";
import {
	getFileExtension,
	getFileName,
	isPartnerApp,
	isVendorApp
} from "@/Utils";
import ZMenu from "@molecules/ZMenu";
import ZDropdown from "@molecules/ZDropdown";
import Row from "antd/lib/row";
import Col from "antd/lib/col";
import "../index.less";
import "./index.less";

const { ZText } = ZTypography;

const ZAddComment: React.FC<ICommentUIProps> = ({
	onSubmit,
	onClose,
	form,
	isLoading = false,
	ZOpportunitySelectFormItem,
	ZClientSelectFormItem,
	ZUserSelectFormItem,
	isSubmitDisabled = false,
	downloadFile,
	removeAttachment,
	openUploadAttachmentModal,
	ZVendorPartnerSelectFormItem,
	attachments,
	handleFormChange,
	isReadOnly,
	isEditMode
}) => {
	const isVendorApplication = isVendorApp();
	const getPinitLabel = () => {
		const noteRelation = form.getFieldValue("relation");
		if (noteRelation === ZomentumEntities.Opportunity)
			return "Pin it to opportunity";
		else if (noteRelation === ZomentumEntities.Client)
			return "Pin it to client";
		return "Pin it";
	};

	const getRelationOptions = useCallback(() => {
		const relationOptions: IRelationOption[] = [];

		if (isVendorApplication) {
			relationOptions.push({
				label: "Partner",
				value: ZomentumEntities.VendorCompany,
				children: (
					<div className="mt-3 sales-activity-relation-options">
						<ZVendorPartnerSelectFormItem disabled={isReadOnly} />
					</div>
				)
			});
		} else {
			relationOptions.push({
				label: "Opportunity",
				value: ZomentumEntities.Opportunity,
				datatestid: "opportunity-radio",
				children: (
					<div className="mt-3 sales-activity-relation-options">
						<ZOpportunitySelectFormItem disabled={isReadOnly} />
					</div>
				)
			});
			relationOptions.push({
				label: "Client",
				value: ZomentumEntities.Client,
				children: (
					<div className="mt-3 sales-activity-relation-options">
						<ZClientSelectFormItem disabled={isReadOnly} />
					</div>
				)
			});
		}
		return relationOptions;
	}, [
		isVendorApplication,
		ZVendorPartnerSelectFormItem,
		ZOpportunitySelectFormItem,
		ZClientSelectFormItem
	]);

	const AttachmentUI = ({
		attachment,
		index
	}: {
		attachment: Attachment;
		index: number;
	}) => (
		<div
			key={index}
			className="inline-flex items-center p-2 bg-geekBlue-1 rounded"
		>
			<ZIcon
				name={Icons.ATTACHMENT}
				size="sm"
				colorVariant={7}
				className="mr-2"
			/>
			<div
				className="inline-block overflow-hidden overflow-ellipsis whitespace-nowrap cursor-pointer"
				style={{ maxWidth: 150 }}
				onClick={() => downloadFile(attachment)}
			>
				<ZText color={EColors.BLUE} colorVariant={6}>
					{getFileName(attachment)}
				</ZText>
			</div>
			<ZText color={EColors.BLUE} colorVariant={6}>
				.{getFileExtension(attachment)}
			</ZText>
			{!isReadOnly && (
				<ZIcon
					name={Icons.CANCEL}
					size="sm"
					className="ml-2 cursor-pointer"
					onClick={() => removeAttachment(attachment)}
				/>
			)}
		</div>
	);

	const UploadAttachments = (
		<Row className="mb-6">
			<Col offset={5} span={19}>
				{!isReadOnly && (
					<div className="mb-2">
						<ZButton
							iconPlacement="left"
							iconName={Icons.UPLOAD}
							onClick={openUploadAttachmentModal}
						>
							Upload Attachments
						</ZButton>
					</div>
				)}
				<div>
					{attachments
						.slice(0, 4)
						.map((attachment: Attachment, index: number) => (
							<div
								key={`attachment-${index}`}
								className="inline-block mr-2 mb-2"
							>
								<AttachmentUI
									attachment={attachment}
									index={index}
								/>
							</div>
						))}
				</div>
				{attachments.length > 4 && (
					<ZDropdown
						trigger={["click"]}
						placement="topLeft"
						overlay={
							<ZMenu>
								{attachments
									.slice(4)
									.map(
										(
											attachment: Attachment,
											index: number
										) => (
											<ZMenu.Item
												key={`attachment_menu_item_${index}`}
											>
												<AttachmentUI
													attachment={attachment}
													index={index}
												/>
											</ZMenu.Item>
										)
									)}
							</ZMenu>
						}
					>
						<div className="inline-flex items-center cursor-pointer px-2 py-1 border border-solid rounded text-gray-9 border-gray-5">
							{attachments.length - 4} more files attached{" "}
							<ZIcon
								name={Icons.CHEVRONUP}
								size="sm"
								className="ml-2"
							/>
						</div>
					</ZDropdown>
				)}
			</Col>
		</Row>
	);

	const sections: Array<IFormSectionProps> = [
		{
			isIndentFormItems: false,
			hideDivider: true,
			getMeta: () => {
				const fields: IFormFieldProps[] = [];
				fields.push({
					key: "body",
					label: "Description",
					widget: EWidgets.INPUTTEXTAREA,
					required: true,
					message: "Please enter notes",
					hasFeedback: true,
					widgetProps: {
						placeholder: "Please enter description",
						autoSize: { minRows: 3, maxRows: 6 },
						allowClear: true,
						disabled: isReadOnly
					},
					rules: [
						{
							whitespace: true,
							message: "Description cannot be empty"
						}
					]
				});
				fields.push({
					key: "attachments",
					render: () => UploadAttachments
				});
				if (isPartnerApp() && !isReadOnly) {
					fields.push({
						key: "relation",
						label: "Related to",
						widget: EWidgets.RADIOGROUP,
						widgetProps: {
							className: "sales-activity-relation-radio",
							space: "middle",
							options: getRelationOptions()
						},
						required: true,
						message: "Please select relation"
					});
				}
				fields.push({
					key: "pinned",
					label: getPinitLabel(),
					widget: EWidgets.SWITCH,
					widgetProps: {
						size: "small",
						disabled: isReadOnly
					}
				});
				if (!isReadOnly) {
					fields.push({
						key: "author_id",
						render: () => (
							<ZUserSelectFormItem
								showAssociatedUsers={
									isEditMode ? isReadOnly : true
								}
								disabled={isReadOnly}
							/>
						)
					});
				}
				return {
					formItemLayout: {
						labelCol: { span: 5 },
						wrapperCol: { span: 19 }
					},
					fields
				};
			}
		}
	];

	const formProps: IFormProps = {
		key: "AddCommentForm",
		form,
		sections,
		disabled: isLoading || isReadOnly,
		onFinish: onSubmit,
		submitButtonText: "Save",
		isRightFooter: true,
		isStickFooter: true,
		isCancelAllowed: true,
		onCancel: onClose,
		isDynamic: true,
		isSubmitDisabled: isSubmitDisabled,
		shouldResetFormOnCancel: false,
		onValuesChange: handleFormChange,
		formFooter: isReadOnly ? <div /> : undefined
	};

	return <ZForm {...formProps} className="py-4 z-comment-form" />;
};

export default ZAddComment;
